import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useParams, useHistory } from 'react-router-dom';

const DramaEdit = () => {
  const { id } = useParams();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drama, setDrama] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    poster: null,
    posterPreview: null,
  });

  const [episodes, setEpisodes] = useState([]);
  const [newEpisodes, setNewEpisodes] = useState([]);

  useEffect(() => {
    fetchDramaData();
  }, [id]);

  const fetchDramaData = async () => {
    try {
      const response = await fetch(`/api/dramas/${id}`);
      const data = await response.json();
      if (data.success) {
        setDrama(data.data);
        setFormData({
          name: data.data.name,
          description: data.data.description,
          isActive: data.data.status === 'active',
          poster: null,
          posterPreview: data.data.poster ? '/' + data.data.poster : null,
        });
        if (data.data.episodes) {
          setEpisodes(data.data.episodes);
        }
      }
    } catch (error) {
      console.error('获取剧集信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          poster: file,
          posterPreview: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEpisodeFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newEpisodeFiles = [];
      for (const file of files) {
        // 对于视频文件，我们使用一个默认的预览图标，或者可以生成缩略图
        // 这里我们使用一个简单的视频图标作为预览
        const newEpisode = {
          file: file,
          preview: '/api/placeholder/150/100', // 使用占位符作为预览
        };
        
        // 尝试为视频文件生成海报
        if (file.type.startsWith('video/')) {
          try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'episode');
            
            const response = await fetch('/api/upload/video', {
              method: 'POST',
              body: formData,
              credentials: 'include', // 添加跨域请求时包含凭证
            });
            
            if (response.ok) {
              const data = await response.json();
              newEpisode.preview = data.posterPath || '/api/placeholder/150/100';
              newEpisode.videoUrl = data.filePath;
              newEpisode.posterUrl = data.posterPath;
            }
          } catch (error) {
            console.error('生成视频海报失败:', error);
            // 如果失败，继续使用默认预览
          }
        }
        
        newEpisodeFiles.push(newEpisode);
      }
      setNewEpisodes(newEpisodeFiles);
    }
  };

  const removeEpisode = async (index) => {
    const episodeToRemove = episodes[index];
    try {
      // 调用服务器删除 API
      const response = await fetch(`/api/dramas/${drama.id}/episodes/${episodeToRemove.episodeNumber}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        // 从状态中移除剧集
        const newEpisodeList = [...episodes];
        newEpisodeList.splice(index, 1);
        setEpisodes(newEpisodeList);
        
        // 显示成功消息
        setSnackbar({ open: true, message: `第${episodeToRemove.episodeNumber}集删除成功`, severity: 'success' });
      } else {
        throw new Error('删除失败');
      }
    } catch (error) {
      console.error('删除剧集失败:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const removeNewEpisode = (index) => {
    const newEpisodeList = [...newEpisodes];
    newEpisodeList.splice(index, 1);
    setNewEpisodes(newEpisodeList);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEditing(true);

    try {
      // 保存基本信息
      const dramaData = {
        name: formData.name,
        description: formData.description,
        status: formData.isActive ? 'active' : 'inactive',
        episodes: episodes,
      };

      // 上传海报
      if (formData.poster) {
        const posterFormData = new FormData();
        posterFormData.append('drama', drama.name);
        posterFormData.append('poster', formData.poster);

        const posterResponse = await fetch(`/api/dramas/${id}/poster`, {
          method: 'POST',
          body: posterFormData,
          credentials: 'include', // 添加跨域请求时包含凭证
        });

        if (!posterResponse.ok) {
          throw new Error('海报上传失败');
        }
      }

      // 上传新剧集
      if (newEpisodes.length > 0) {
        // 对于每个新剧集，我们已经在 handleEpisodeFileChange 中上传了
        // 现在我们只需要将它们添加到剧集列表中
        const newEpisodeData = [];
        for (let i = 0; i < newEpisodes.length; i++) {
          const episode = newEpisodes[i];
          newEpisodeData.push({
            episodeNumber: episodes.length + i + 1,
            title: `第${episodes.length + i + 1}集`,
            videoUrl: episode.videoUrl,
            poster: episode.posterUrl,
          });
        }
        
        const episodeFormData = new FormData();
        episodeFormData.append('drama', drama.name);
        episodeFormData.append('episodes', JSON.stringify(newEpisodeData));
        
        const episodeResponse = await fetch(`/api/dramas/${id}/episodes`, {
          method: 'POST',
          body: episodeFormData,
          credentials: 'include', // 添加跨域请求时包含凭证
        });
        
        if (!episodeResponse.ok) {
          const errorData = await episodeResponse.json();
          throw new Error(errorData.message || '剧集上传失败');
        }
        
        // 上传成功后，直接使用服务器返回的全量剧集列表
        const episodeResult = await episodeResponse.json();
        if (episodeResult.success) {
          // 直接使用服务器返回的剧集列表（不要合并，否则会重复）
          dramaData.episodes = episodeResult.episodes;
          dramaData.totalEpisodes = episodeResult.totalEpisodes;
        }
      }

      // 更新剧集信息
      const response = await fetch(`/api/dramas/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // 添加跨域请求时包含凭证
        body: JSON.stringify(dramaData),
      });

      if (response.ok) {
        setSnackbar({ open: true, message: '保存成功', severity: 'success' });
        setTimeout(() => {
          history.push('/admin');
        }, 2000);
      } else {
        throw new Error('保存失败');
      }
    } catch (error) {
      console.error('保存失败:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setEditing(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Container maxWidth="lg" sx={{ 
          mt: 3, 
          mb: 4, 
          px: isMobile ? 1 : 2,
        }}>
          <Typography variant="h6">加载中...</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="lg" sx={{ 
        mt: 3, 
        mb: 4, 
        px: isMobile ? 1 : 2,
      }}>
        <Box sx={{ mt: isMobile ? 1 : 4, mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/admin')}
            sx={{ mb: 2 }}
          >
            返回管理页面
          </Button>

          <Paper elevation={3} sx={{ p: isMobile ? 2 : 4 }}>
            <Typography variant={isMobile ? "h6" : "h4"} component="h1" gutterBottom>
              编辑剧集
            </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="短剧名称"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="短剧介绍"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              required
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="是否活跃"
              sx={{ mt: 2, mb: 2 }}
            />

            <Box sx={{ mt: 2, mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                短剧海报
              </Typography>
              <Button
                variant="contained"
                component="label"
                fullWidth
                startIcon={<UploadIcon />}
                sx={{ mb: 2 }}
              >
                上传海报
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
              {formData.posterPreview && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <img
                    src={formData.posterPreview}
                    alt="海报预览"
                    style={{
                      maxWidth: '300px',
                      maxHeight: '400px',
                      objectFit: 'contain',
                      borderRadius: 8,
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 4, mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                短剧剧集 ({episodes.length + newEpisodes.length} 集)
              </Typography>
              
              <Button
                variant="contained"
                component="label"
                fullWidth
                startIcon={<UploadIcon />}
                sx={{ mb: 2 }}
              >
                上传剧集
                <input
                  type="file"
                  hidden
                  accept="video/*"
                  multiple
                  onChange={handleEpisodeFileChange}
                />
              </Button>

              <Grid container spacing={2}>
                {/* 已上传的剧集 */}
                {episodes.map((episode, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card elevation={2}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={episode.poster || '/api/placeholder/150/100'}
                        alt={`第${episode.episodeNumber}集`}
                        sx={{ objectFit: 'contain' }}
                      />
                      <CardContent>
                        <Typography gutterBottom variant="h6" component="div">
                          第{episode.episodeNumber}集
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {episode.title || '无标题'}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label="删除"
                            color="error"
                            size="small"
                            onClick={() => removeEpisode(index)}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {/* 新上传的剧集 */}
                {newEpisodes.map((episode, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card elevation={2}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={episode.preview}
                        alt={`新集${index + 1}`}
                        sx={{ objectFit: 'contain' }}
                      />
                      <CardContent>
                        <Typography gutterBottom variant="h6" component="div">
                          第{episodes.length + index + 1}集
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {episode.file.name}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label="删除"
                            color="error"
                            size="small"
                            onClick={() => removeNewEpisode(index)}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => history.push('/admin')}
                disabled={editing}
              >
                取消
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={editing}
                startIcon={editing ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {editing ? '保存中...' : '保存'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Container>
    </Box>
  );
};

export default DramaEdit;
